import { ArrowFatLeft, ArrowFatRight, List } from "@phosphor-icons/react";
import clsx from "clsx";
import { motion } from "motion/react";
import { useRef, useState } from "react";
import usePreloadImage from "../hooks/use-preload-image";
import Tappable from "./tappable";

const STEPS: { src: string; label: React.ReactNode }[] = [
  {
    src: "/tutorial/1.png",
    label: (
      <>
        Open your profile and tap{" "}
        <List weight="bold" className="inline-block" />
      </>
    ),
  },
  {
    src: "/tutorial/2.png",
    label: <>Tap “Your activity”</>,
  },
  {
    src: "/tutorial/3.png",
    label: <>Scroll to “Download your info”</>,
  },
  {
    src: "/tutorial/4.png",
    label: <>Tap “Download or transfer info”</>,
  },
  {
    src: "/tutorial/5.png",
    label: <>Select your Instagram account</>,
  },
  {
    src: "/tutorial/6.png",
    label: <>Tap “All available information”</>,
  },
  {
    src: "/tutorial/7.png",
    label: <>Tap “Download to device”</>,
  },
  {
    src: "/tutorial/8.png",
    label: <>Tap “Format”</>,
  },
  {
    src: "/tutorial/9.png",
    label: <>Select “JSON”</>,
  },
  {
    src: "/tutorial/10.png",
    label: <>Tap “Create files”</>,
  },
  {
    src: "/tutorial/11.png",
    label: <>When you get the email, tap “Download your information”</>,
  },
  {
    src: "/tutorial/12.png",
    label: <>Tap “Download”</>,
  },
];

export default function Tutorial({
  onNext,
  startAtEnd,
}: {
  onNext: () => void;
  startAtEnd: boolean;
}) {
  const initialStep = startAtEnd ? STEPS.length - 1 : 0;
  const [currentStep, setCurrentStep] = useState(initialStep);

  const direction = useRef<"left" | "right">(startAtEnd ? "left" : "right");

  const step = STEPS[currentStep];
  const nextStep = STEPS[currentStep + 1];

  const onFirstStep = currentStep === 0;
  const onLastStep = currentStep === STEPS.length - 1;

  usePreloadImage(nextStep?.src);

  return (
    <div className="grid grid-rows-[max-content_1fr_max-content_max-content] h-full overflow-hidden">
      <div className="pb-1 bg-brand-lighter-pink shadow-sm shadow-brand-dark-pink/5">
        <div className="px-2 pt-4 pb-3 border-b-2 border-brand-dark-pink/20 border-dashed">
          <p className="text-lg bg-brand-muddy-light-pink w-max text-brand-dark-pink font-bold text-center mx-auto px-4 py-0.5 rounded-full">
            Step 1
          </p>
          <p className="mt-1 text-2xl text-brand-dark-pink font-bold text-center">
            Download your IG data
          </p>
        </div>
      </div>

      <div className="mt-2 overflow-hidden">
        <motion.div
          key={step.src}
          initial={{ opacity: 0, x: direction.current === "left" ? -100 : 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction.current === "left" ? 100 : -100 }}
          className="overflow-hidden h-full"
        >
          <img src={step.src} alt="" className="h-full mx-auto" />
        </motion.div>
      </div>

      <div className="mt-2">
        <motion.p
          key={step.src}
          initial={{ opacity: 0, x: direction.current === "left" ? -100 : 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction.current === "left" ? 100 : -100 }}
          className="text-2xl text-center text-brand-dark-pink font-bold"
        >
          {step.label}
        </motion.p>
        <div className="mt-4 w-max mx-auto flex items-center gap-x-2">
          {STEPS.map((_, index) => (
            <div
              key={index}
              className={clsx(
                "rounded-full transition-all",
                index === currentStep
                  ? "size-2 bg-brand-dark-pink"
                  : "size-1.5 bg-brand-dark-pink/40"
              )}
            />
          ))}
        </div>
      </div>

      <div
        className={clsx(
          "mt-8 px-4 pb-4",
          !onFirstStep && "grid grid-cols-[max-content_1fr] gap-x-2"
        )}
      >
        {!onFirstStep && (
          <Tappable>
            <button
              className="w-full bg-brand-muddy-light-pink rounded-2xl text-brand-dark-pink flex items-center justify-center gap-x-2 py-2 px-6"
              onClick={() => {
                direction.current = "left";
                setCurrentStep((step) => step - 1);
              }}
            >
              <ArrowFatLeft size={18} weight="fill" />
              <p className="text-lg font-semibold">Back</p>
            </button>
          </Tappable>
        )}

        <Tappable>
          <button
            className="w-full bg-brand-dark-pink rounded-2xl text-white flex items-center justify-center gap-x-2 py-2"
            onClick={() => {
              direction.current = "right";
              if (onLastStep) {
                onNext();
              } else {
                setCurrentStep((step) => step + 1);
              }
            }}
          >
            <p className="text-lg font-semibold">Next</p>
            <ArrowFatRight size={18} weight="fill" />
          </button>
        </Tappable>
      </div>
    </div>
  );
}
